// @ts-check
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { cloneByCopy } from './cloneByCopy';
import { getModelWithLpTags } from './getModelWithLpTags';

/**
 * @template {import('../types').BaseT} Props The Generic shape of the model to fetch. @example CSModelProps
 * @typedef {object} LivePreviewStateData
 * @property {Props} staticModel
 * @property {Props} previewModel
 * @property {string} contentType
 * @property {string} locale
 */
/**
 * @template {import('../types').BaseT} Props The Generic shape of the model to fetch. @example CSModelProps
 * @typedef {object} ComputedLivePreviewStateData
 * @property {boolean} isLivePreviewModel
 * @property {Props} staticModelStripped
 * @property {import('../types').ModelWithLivePreviewAttr<Props>} staticModelWithTags
 * @property {import('../types').ModelWithLivePreviewAttr<Props>} previewModelWithTags
 */
/**
 * @template {import('../types').BaseT} Props The Generic shape of the model to fetch. @example CSModelProps
 * @typedef {LivePreviewStateData<Props> & ComputedLivePreviewStateData<Props>} LivePreviewState
 */

/**
 * @template {import('../types').BaseT} Props The Generic shape of the model to fetch. @example CSModelProps
 * @function CreateLivePreviewState
 * @param {LivePreviewStateData<Props>} createOptions
 * @returns {LivePreviewState<Props>}
 */
function CreateLivePreviewState(createOptions) {
	return {
		...createOptions,

		...getIsLivePreviewModel(createOptions.staticModel, createOptions.previewModel),

		staticModelWithTags: getModelWithLpTags(
			createOptions.staticModel,
			createOptions.contentType,
			true,
			createOptions.locale
		),
		previewModelWithTags: getModelWithLpTags(
			createOptions.previewModel,
			createOptions.contentType,
			true,
			createOptions.locale
		),
	};
}

/**
 * @template {import('../types').BaseT} Props The Generic shape of the model to fetch. @example CSModelProps
 * @param {Props} staticModel
 * @param {Props} previewModel
 * @returns {{ isLivePreviewModel: boolean, staticModelStripped: Props, previewModelStripped: Props }}
 */
function getIsLivePreviewModel(staticModel, previewModel) {
	const previewModelStripped = cloneByCopy(previewModel, previewModel);
	const staticModelStripped = cloneByCopy(staticModel, staticModel);

	return {
		isLivePreviewModel: !isEqual(staticModelStripped, previewModelStripped),
		previewModelStripped,
		staticModelStripped,
	};
}

/**
 * @template {import('../types').BaseT} Props The Generic shape of the model to fetch. @example CSModelProps
 * @typedef {object} CurrentPropsData
 * @property {Props} currentProps
 * @property {import('../types').ModelWithLivePreviewAttr<Props>} currentPropsWith$
 */
/**
 * @template {import('../types').BaseT} Props The Generic shape of the model to fetch. @example CSModelProps
 * @function GetCurrentPropsFunc
 * @param {LivePreviewState<Props>} state
 * @returns {CurrentPropsData<Props>}
 */
function GetCurrentPropsData(state) {
	return {
		currentProps: state.isLivePreviewModel ? state.previewModel : state.staticModel,
		currentPropsWith$: state.isLivePreviewModel ? state.previewModelWithTags : state.staticModelWithTags,
	};
}

/**
 * @template {import('../types').BaseT} Props The Generic shape of the model to fetch. @example CSModelProps
 * @function UpdatePreviewModelFunc
 * @param {LivePreviewState<Props>} state
 * @param {Props} updated
 * @returns {LivePreviewState<Props>}
 */
function UpdatePreviewModel(state, updated) {
	return {
		...cloneDeep(state),

		...getIsLivePreviewModel(state.staticModel, updated),

		previewModel: updated,
		previewModelWithTags: getModelWithLpTags(updated, state.contentType, true, state.locale),
	};
}

export const LivePreview = {
	Create: CreateLivePreviewState,
	getCurrent: GetCurrentPropsData,
	Update: UpdatePreviewModel,
};